import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrlApi } from "../../proxy";
import CAxios from "../utils/CAxios";
import axios, { AxiosResponse } from "axios";
import { resolveServicesVehicleId } from "../../utils/resolveServicesVehicleId";
import { getCartErrorMessage, getCartResponseError } from "../../utils/cartResponse";
import {
  isSameShopListRequest,
  resolveCategoryId,
  resolveListLimit,
  resolveListPage,
  resolveListSort,
  ShopListRequest,
} from "../../utils/paginatedList";

interface ReviewPayload {
  product_id: number;
  rate: number;
  message: string;
}

export type ServiceListArgs = {
  vehicleId: number;
  lang: string;
  page?: number;
  limit?: number;
  categoryId?: string | number | null;
  sort?: string | null;
};

export type ProductListArgs = {
  lang: string;
  page?: number;
  limit?: number;
  categoryId?: string | number | null;
  sort?: string | null;
};

type ListSliceState = {
  status?: string;
  request?: ShopListRequest;
};

export const toGetService = createAsyncThunk<
  AxiosResponse<any, any>,
  ServiceListArgs
>(
  "vehicle/services",
  async ({ vehicleId, lang, page, limit, categoryId, sort }) => {
    try {
      const categId = resolveCategoryId(categoryId);
      const sortValue = resolveListSort(sort);
      const response = await axios.post(
        `${baseUrlApi}services/list?lang=${lang}`,
        {
          vehicle_id: resolveServicesVehicleId(vehicleId),
          page: resolveListPage(page),
          limit: resolveListLimit(limit),
          // Only the POST body is honoured here; `?categ_id=` is ignored.
          ...(categId != null ? { categ_id: categId } : {}),
          // `sort` is only honoured in the body here too; `?sort=` is ignored.
          ...(sortValue ? { sort: sortValue } : {}),
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  },
  {
    condition: (arg, { getState }) => {
      const state = getState() as {
        services?: { services?: ListSliceState };
      };
      const current = state.services?.services;
      if (current?.status !== "loading") return true;
      return !isSameShopListRequest(current.request, {
        lang: arg.lang,
        page: resolveListPage(arg.page),
        limit: resolveListLimit(arg.limit),
        vehicleId: resolveServicesVehicleId(arg.vehicleId),
        categoryId: resolveCategoryId(arg.categoryId),
        sort: resolveListSort(arg.sort),
      });
    },
  }
);

export const toGetProducts = createAsyncThunk<
  AxiosResponse<any, any>,
  ProductListArgs
>(
  "products",
  async ({ lang, page, limit, categoryId, sort }) => {
    try {
      const response = await axios.get(`${baseUrlApi}products?lang=${lang}`, {
        params: {
          page: resolveListPage(page),
          limit: resolveListLimit(limit),
          categ_id: resolveCategoryId(categoryId),
          sort: resolveListSort(sort),
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
  {
    condition: (arg, { getState }) => {
      const state = getState() as {
        services?: { products?: ListSliceState };
      };
      const current = state.services?.products;
      if (current?.status !== "loading") return true;
      return !isSameShopListRequest(current.request, {
        lang: arg.lang,
        page: resolveListPage(arg.page),
        limit: resolveListLimit(arg.limit),
        categoryId: resolveCategoryId(arg.categoryId),
        sort: resolveListSort(arg.sort),
      });
    },
  }
);

export const toGetDetails = createAsyncThunk<
  AxiosResponse<any, any>,
  { id: number; lang: string }
>("products/details", async ({ id, lang }) => {
  try {
    const response = await axios.get(
      `${baseUrlApi}services/${id}?lang=${lang}`
    );
    return response;
  } catch (error) {
    throw error;
  }
});

export const toGetServiceProductVariants = createAsyncThunk<
  AxiosResponse<any, any>,
  {
    serviceId: number;
    vehicleId: unknown;
    lang: string;
    forceRefresh?: boolean;
  }
>(
  "service/productVariants",
  async ({ serviceId, vehicleId, lang }) => {
    try {
      const response = await axios.post(
        `${baseUrlApi}services/${serviceId}/product-variants?lang=${lang}`,
        {
          vehicle_id: resolveServicesVehicleId(vehicleId),
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg.forceRefresh) return true;

      const state = getState() as {
        services?: {
          serviceVariants?: {
            status?: string;
            serviceId?: number | null;
            vehicleId?: number | null;
            lang?: string | null;
          };
        };
      };
      const variants = state.services?.serviceVariants;
      if (variants?.status === "loading") return false;

      const vehicleId = resolveServicesVehicleId(arg.vehicleId);
      if (
        variants?.status === "succedded" &&
        variants.serviceId === arg.serviceId &&
        variants.vehicleId === vehicleId &&
        variants.lang === arg.lang
      ) {
        return false;
      }

      return true;
    },
  }
);

export const toAddReview = createAsyncThunk(
  "service/toAddReview",
  async (data: ReviewPayload) => {
    try {
      const response = await CAxios.post(`services/review`, data);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

export const toAddShop = createAsyncThunk(
  "service/toAddShop",
  async (data: ReviewPayload, { rejectWithValue }) => {
    try {
      const response = await CAxios.post(`shop`, data);
      const error = getCartResponseError(response.data);
      if (error !== undefined) return rejectWithValue(error);
      return response;
    } catch (error) {
      return rejectWithValue(getCartErrorMessage(error));
    }
  }
);

export const toRemoveCoupon = createAsyncThunk(
  "service/remove_coupon",
  async (data: ReviewPayload) => {
    try {
      const response = await CAxios.post(`shop/remove_coupon`, data);
      return response;
    } catch (error) {
      throw error;
    }
  }
);
